import { Model, Project, Scenario, ScenarioModel } from "@api/apiClient";
import isGuid from "@utils/isGuid";
import mitt from "mitt";
import { computed, makeAutoObservable, toJS } from "mobx";

export enum ViewerIds {
  Viewer1 = "viewer1",
  Viewer2 = "viewer2",
}

export type ViewerId = ViewerIds.Viewer1 | ViewerIds.Viewer2;

export interface Selection {
  viewerId: ViewerId;
  modelIds: string[];
}

export enum SelectionMode {
  SingleModel = "SingleModel",
  SingleScenarioSingleModel = "SingleScenarioSingleModel",
  SingleScenarioMultiModel = "SingleScenarioMultiModel",
  DualScenariosMultiModel = "DualScenariosMultiModel",
}

export enum SelectionManagerEvents {
  SELECTION_ONLOAD_EVENT = "onLoad",
  SELECTION_ONUNLOAD_EVENT = "onUnLoad",
  SELECTION_MODE_CHANGED_EVENT = "selectionModeChanged",
  SELECTION_CHANGED_EVENT = "selectionChanged",
}

/**
 * Dummy scenario ID used for SingleModel mode where no real scenario is selected.
 * This allows reusing the selections map infrastructure for model-only selection.
 */
export const SINGLE_MODEL_SCENARIO_ID = "__single_model__";

/**
 * Manages scenario and model selections for the various Project Analysis tabs.
 * Element selection is managed separately via the DataGrid internal selection model.
 */
export class SelectionManager {
  selectionMode: SelectionMode = SelectionMode.SingleScenarioSingleModel;
  selections: Record<string, Selection> = {};
  emitter: any = mitt(); // f-ts
  private selectionModeRequestId = 0;

  project: Project | null = null;
  scenarios: Scenario[] | null = null;

  constructor() {
    makeAutoObservable(
      this,
      {
        selectCount: computed,
      },
      { autoBind: true },
    );
  }

  get dump() {
    return toJS(this);
  }

  get selectedProject(): Project | null {
    return this.project;
  }

  get selectedScenario(): Scenario | null {
    if (this.selectionMode === SelectionMode.SingleModel) {
      return null;
    }
    const scenarioId = this.scenarioIds[0];
    return this.scenarios?.find((s: Scenario) => s.scenarioId === scenarioId) ?? null;
  }

  /**
   * Gets the active scenario ID for selection operations.
   * In SingleModel mode, returns SINGLE_MODEL_SCENARIO_ID.
   * In SingleScenarioSingleModel or SingleScenarioMultiModel modes, returns the actual selected scenario ID.
   * In DualScenariosMultiModel mode, returns the first selected scenario ID.
   */
  get selectedScenarioId(): string | undefined {
    if (Object.keys(this.selections).length > 0) {
      return Object.keys(this.selections)[0];
    } else {
      return undefined;
    }
  }

  get selectedModel(): Model | null {
    if (this.selectionMode === SelectionMode.SingleModel) {
      const modelId = this.selections[SINGLE_MODEL_SCENARIO_ID]?.modelIds[0];
      return this.project?.models?.find((m: Model) => m.modelId === modelId) ?? null;
    }
    const modelId = this.selections[this.scenarioIds[0]]?.modelIds[0];
    return this.selectedScenario?.scenarioModels?.find((sm: ScenarioModel) => sm.modelId === modelId)?.model ?? null;
  }

  get scenarioIds(): string[] {
    return Object.keys(this.selections);
  }

  get selectCount(): number {
    return Object.keys(this.selections).reduce((acc, scenarioId) => acc + this.selections[scenarioId].modelIds.length, 0);
  }

  get maxScenarioSelections(): number {
    switch (this.selectionMode) {
      case SelectionMode.SingleModel:
        return 1;
      case SelectionMode.SingleScenarioSingleModel:
        return 1;
      case SelectionMode.SingleScenarioMultiModel:
        return 1;
      case SelectionMode.DualScenariosMultiModel:
        return 2;
      default:
        return 1;
    }
  }

  /**
   * Checks if a scenarioId is the dummy single-model entry.
   * Useful for filtering out the dummy ID when iterating scenarioIds.
   */
  isSingleModelSelection(scenarioId: string): boolean {
    return scenarioId === SINGLE_MODEL_SCENARIO_ID;
  }

  // -----------------------
  // Project/Scenario/Model
  // -----------------------
  /**
   * Clear all current selections and reset scenario/model.
   * Ensures unload side-effects run and emits a single change event.
   */
  async clearSelection(): Promise<void> {
    await this.unSelectAllScenarios(false);
    this.setSelectedModel(null);
    this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
  }

  setProject(project: Project | null | undefined) {
    this.project = project ?? null;
    //  TODO: reset selections if project id changes
  }

  setScenarios(scenarios: Scenario[] | null) {
    this.scenarios = scenarios ?? null;
    this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
    //  TODO: reset selections if selected scenario(s) no longer exist
  }

  setSelectedModel(modelId: string | null) {
    if (modelId) {
      this.selections[this.scenarioIds[0]] = {
        viewerId: ViewerIds.Viewer1,
        modelIds: [modelId],
      };
    } else {
      delete this.selections[this.scenarioIds[0]];
    }
    this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
  }

  setDefaultModel() {
    const models = this.selectedProject?.models || [];
    if (models.length > 0 && models[0].modelId) {
      this.setSelectedModel(models[0].modelId);
    } else {
      this.setSelectedModel(null);
    }
  }

  // TODO: change these to use mitt events
  // Note: onLoad/onUnload still receive URNs (for viewer compatibility)
  // SelectionManager converts modelIds → URNs before calling these callbacks
  onLoad: (scenarioId: string, viewerId: ViewerId, urns: string | string[]) => Promise<void> = () => {
    return new Promise<void>((resolve) => resolve());
  };

  // TODO: change these to use mitt events
  // Note: onLoad/onUnload still receive URNs (for viewer compatibility)
  // SelectionManager converts modelIds → URNs before calling these callbacks
  onUnload: (scenarioId: string, viewerId: ViewerId, urns: string | string[]) => Promise<void> = () => {
    return new Promise<void>((resolve) => resolve());
  };

  /**
   * Converts modelIds to URNs by looking up models in project.models
   */
  private modelIdsToUrns(modelIds: string[]): string[] {
    if (!this.selectedProject?.models || modelIds.length === 0) return [];
    return this.selectedProject.models
      .filter((m: Model) => modelIds.includes(m.modelId!) && m.apsViewId)
      .map((m: Model) => m.apsViewId!);
  }

  /**
   * Converts URNs to modelIds by looking up models in project.models.
   * Works in both SingleModel and MultiModel selection modes.
   */
  urnsToModelIds(urns: string[]): string[] {
    if (!this.selectedProject?.models || urns.length === 0) return [];
    return this.selectedProject.models
      .filter((m: Model) => urns.includes(m.apsViewId!) && m.modelId)
      .map((m: Model) => m.modelId!);
  }

  /**
   * Converts an identifier (URN or modelId) to a modelId.
   * If the identifier is a GUID, it's treated as a modelId.
   * Otherwise, it's treated as a URN and converted to modelId.
   */
  private toModelId(identifier: string): string | undefined {
    if (isGuid(identifier)) {
      return identifier; // Already a modelId
    }
    // It's a URN - convert to modelId
    const modelIds = this.urnsToModelIds([identifier]);
    return modelIds[0];
  }

  /**
   * Converts an array of identifiers (URNs or modelIds) to modelIds.
   */
  private toModelIds(identifiers: string[]): string[] {
    return identifiers
      .map((id) => this.toModelId(id))
      .filter((id): id is string => id !== undefined);
  }

  getSelections(index: number): Selection {
    const scenarioId: string = this.scenarioIds[index];
    return this.selections[scenarioId];
  }

  getScenarioIdByViewerId(viewerId: ViewerId): string | undefined {
    return Object.keys(this.selections).find((key: string) => this.selections[key].viewerId === viewerId);
  }

  /*
  * Converts a viewerIndex [0, 1] to a viewerId [Viewer1, Viewer2]
  * then does a lookup to find the scenarioId
  */
  getScenarioIdByViewerIndex(viewerIndex: number): string | undefined {
    const viewerId = viewerIndex === 0 ? ViewerIds.Viewer1 : ViewerIds.Viewer2;
    return Object.keys(this.selections).find((key: string) => this.selections[key].viewerId === viewerId);
  }

  getViewerIdByScenarioId(scenarioId: string): ViewerId | undefined {
    return this.selections[scenarioId]?.viewerId;
  }

  /**
   * Gets the selected model IDs for a scenario (internal storage format).
   * Use this for tree building, OPFS operations, and backend API calls.
   */
  getSelectedModelIds(scenarioId?: string): string[] {
    scenarioId = scenarioId ?? this.selectedScenario?.scenarioId!;
    const selection = this.selections[scenarioId];
    return selection?.modelIds || [];
  }

  /**
   * Gets the selected model URNs for a scenario (converted from modelIds).
   * Use this for viewer operations (loadDocument, unloadDocument).
   */
  getSelectedModelUrns(scenarioId?: string): string[] {
    scenarioId = scenarioId ?? this.selectedScenario?.scenarioId!;
    const modelIds = this.getSelectedModelIds(scenarioId);
    return this.modelIdsToUrns(modelIds);
  }

  /**
   * Gets scenario models that are currently selected (by modelId).
   */
  getSelectedScenarioModels(scenarioId?: string): ScenarioModel[] {
    scenarioId = scenarioId ?? this.selectedScenario?.scenarioId;
    if (!scenarioId) return [];
    const scenario =
      this.scenarios?.find((s: Scenario) => s.scenarioId === scenarioId) ?? this.selectedScenario;
    const scenarioModels = scenario?.scenarioModels ?? [];
    const selectedIds = this.getSelectedModelIds(scenarioId);
    const idSet = new Set(selectedIds);
    return scenarioModels.filter((sm: ScenarioModel) => sm.modelId != null && idSet.has(sm.modelId));
  }

  /**
   * Gets all model IDs in a scenario (from scenarioModels). Unique per model.
   */
  getScenarioModelIds(scenarioId?: string): string[] {
    const scenario =
      scenarioId != null
        ? this.scenarios?.find((s: Scenario) => s.scenarioId === scenarioId)
        : this.selectedScenario;
    const scenarioModels = scenario?.scenarioModels ?? [];
    return scenarioModels.map((sm: ScenarioModel) => sm.modelId).filter((id): id is string => Boolean(id));
  }

  /**
   * Gets all model URNs available in a scenario (from scenarioModels).
   * Use getSelectedModelUrns() for currently selected models only.
   */
  getScenarioModelUrns(scenarioId?: string): string[] {
    const scenario = scenarioId
      ? this.scenarios?.find((s: Scenario) => s.scenarioId === scenarioId)
      : this.selectedScenario;

    if (!scenario?.scenarioModels || !this.selectedProject?.models) return [];

    return scenario.scenarioModels
      .map((sm: ScenarioModel) =>
        this.selectedProject?.models?.find((m: Model) => m.modelId === sm.modelId)?.apsViewId
      )
      .filter((u): u is string => Boolean(u));
  }

  /**
   * Checks if all models in the scenario are selected.
   */
  isAllModelsSelected(scenarioId?: string): boolean {
    const allUrns = this.getScenarioModelUrns(scenarioId);
    if (allUrns.length === 0) return false;
    const selectedUrns = this.getSelectedModelUrns(scenarioId);
    return allUrns.every((u: string) => selectedUrns.includes(u));
  }

  /**
   * Checks if some (but not all) models in the scenario are selected.
   */
  isSomeModelsSelected(scenarioId?: string): boolean {
    const allUrns = this.getScenarioModelUrns(scenarioId);
    if (allUrns.length === 0) return false;
    const selectedUrns = this.getSelectedModelUrns(scenarioId);
    const allSelected = allUrns.every((u: string) => selectedUrns.includes(u));
    const someSelected = allUrns.some((u: string) => selectedUrns.includes(u));
    return !allSelected && someSelected;
  }

  setSelectionMode(mode: SelectionMode): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        const requestId = ++this.selectionModeRequestId;
        console.log("SelectionManager.setMode: ", mode);
        if (mode !== this.selectionMode) {
          await this.unSelectAllScenarios();
          if (requestId !== this.selectionModeRequestId) {
            resolve();
            return;
          }
          this.selectionMode = mode;
        }
        resolve();
      })();
    });
  }

  selectScenario(scenarioId: string, urns?: string | string[], emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        // Convert URNs to modelIds for internal storage
        const urnArray = !Array.isArray(urns) ? (urns ? [urns] : []) : urns;
        const modelIds = this.urnsToModelIds(urnArray);

        if (this.selectionMode === SelectionMode.SingleModel ||
          this.selectionMode === SelectionMode.SingleScenarioSingleModel ||
          this.selectionMode === SelectionMode.SingleScenarioMultiModel) {
          const hasScenario = Object.hasOwnProperty.call(this.selections, scenarioId);
          if (!hasScenario) {
            await this.unSelectAllScenarios();
          }

          // Unload URNs that are being removed (in previous selection but not in new set)
          const existing = this.selections[scenarioId];
          if (existing) {
            const toUnload = existing.modelIds.filter((id: string) => !modelIds.includes(id));
            if (toUnload.length > 0) {
              const urnsToUnload = this.modelIdsToUrns(toUnload);
              await this.onUnload(scenarioId, existing.viewerId, urnsToUnload);
            }
          }

          this.selections[scenarioId] = {
            viewerId: ViewerIds.Viewer1,
            modelIds,
          };

          if (modelIds.length > 0) {
            // Convert back to URNs for viewer operations
            const urnsForViewer = this.modelIdsToUrns(modelIds);
            await this.onLoad(scenarioId, ViewerIds.Viewer1, urnsForViewer);
          }
        }
        if (this.selectionMode === SelectionMode.DualScenariosMultiModel) {
          if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
            const selection = this.selections[scenarioId];
            const toUnload = selection.modelIds.filter((id: string) => !modelIds.includes(id));
            if (toUnload.length > 0) {
              const urnsToUnload = this.modelIdsToUrns(toUnload);
              await this.onUnload(scenarioId, selection.viewerId, urnsToUnload);
            }
            selection.modelIds = modelIds.concat(selection.modelIds.filter((id) => !modelIds.includes(id)));
            if (selection.modelIds.length > 0) {
              // Convert back to URNs for viewer operations
              const urnsForViewer = this.modelIdsToUrns(selection.modelIds);
              await this.onLoad(scenarioId, selection.viewerId, urnsForViewer);
            }
          } else {
            // Determine the viewer to target
            let selection;
            try {
              selection = this.selections[Object.keys(this.selections)[0]];
            } catch {
              /*nada*/
            }

            let viewerId: ViewerId = ViewerIds.Viewer1;
            if (selection) {
              viewerId = selection.viewerId === ViewerIds.Viewer1 ? ViewerIds.Viewer2 : ViewerIds.Viewer1;
            }

            this.selections[scenarioId] = {
              viewerId,
              modelIds,
            };

            if (modelIds.length > 0) {
              // Convert back to URNs for viewer operations
              const urnsForViewer = this.modelIdsToUrns(modelIds);
              await this.onLoad(scenarioId, viewerId, urnsForViewer);
            }
          }
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
        }

        console.log("SelectionManager.selections: ", toJS(this.selections));
        resolve();
      })();
    });
  }

  unSelectScenario(scenarioId: string, emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        const selection = this.selections[scenarioId];
        if (selection) {
          // Convert modelIds to URNs for viewer operations
          const urnsForViewer = this.modelIdsToUrns(selection.modelIds);
          await this.onUnload(scenarioId, selection.viewerId, urnsForViewer);
          delete this.selections[scenarioId];

          if (emit) {
            this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
          }
        }

        console.log("SelectionManager.selections: ", toJS(this.selections));
        resolve();
      })();
    });
  }

  unSelectAllScenarios(emit: boolean = false): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        // Object.keys(this.selections).map(async (key) => {
        //   await this.unSelectScenario(key, false);
        // });

        await Promise.all(Object.keys(this.selections).map(async (key) => {
          await this.unSelectScenario(key, false);
        }));

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
        }

        console.log("SelectionManager.selections: ", toJS(this.selections));
        resolve();
      })();
    });
  }

  /**
   * Selects a single model in SingleModel mode.
   * @param identifier - Can be either a modelId (GUID) or a URN
   * @param emit - Whether to emit the selection changed event
   */
  selectModel(identifier: string, emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (this.selectionMode === SelectionMode.SingleModel) {
          await this.unSelectAllScenarios(false);

          // Convert identifier to modelId (handles both URN and modelId)
          const modelId = this.toModelId(identifier);

          if (modelId) {
            this.selections[SINGLE_MODEL_SCENARIO_ID] = {
              viewerId: ViewerIds.Viewer1,
              modelIds: [modelId],
            };

            // Convert modelId to URN for viewer operations
            const urnsForViewer = this.modelIdsToUrns([modelId]);
            if (urnsForViewer.length > 0) {
              await this.onLoad(SINGLE_MODEL_SCENARIO_ID, ViewerIds.Viewer1, urnsForViewer);
            }
          }

          if (emit) {
            this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
          }
        }

        resolve();
      })();
    });
  }

  /**
   * Selects one or more models for a scenario.
   * @param scenarioId - The scenario ID
   * @param identifiers - Can be URNs or modelIds (or a mix)
   * @param emit - Whether to emit the selection changed event
   */
  selectModels(scenarioId: string, identifiers: string | string[], emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        // Convert identifiers to modelIds for internal storage (handles both URNs and modelIds)
        const identifierArray = !Array.isArray(identifiers) ? (identifiers ? [identifiers] : []) : identifiers;
        const modelIds = this.toModelIds(identifierArray);

        if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
          const selection = this.selections[scenarioId];

          if (this.selectionMode === SelectionMode.SingleScenarioSingleModel) {
            // Find modelIds to unload (those not in the new selection)
            const unloadModelIds = selection.modelIds.filter((id: string) => !modelIds.includes(id));
            if (unloadModelIds.length > 0) {
              const unloadUrns = this.modelIdsToUrns(unloadModelIds);
              await this.onUnload(scenarioId, selection.viewerId, unloadUrns);
            }
            selection.modelIds = [];
          }

          // Add new modelIds (avoiding duplicates)
          selection.modelIds = modelIds.concat(selection.modelIds.filter((id: string) => !modelIds.includes(id)));

          if (selection.modelIds.length > 0) {
            // Convert back to URNs for viewer operations
            const urnsForViewer = this.modelIdsToUrns(selection.modelIds);
            await this.onLoad(scenarioId, selection.viewerId, urnsForViewer);
          }
        } else {
          // Pass URNs to selectScenario (it expects URNs)
          const urns = this.modelIdsToUrns(modelIds);
          await this.selectScenario(scenarioId, urns, false);
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
        }

        resolve();
      })();
    });
  }

  /**
   * Unselects one or more models from a scenario.
   * @param scenarioId - The scenario ID
   * @param identifiers - Can be URNs or modelIds (or a mix)
   * @param emit - Whether to emit the selection changed event
   */
  unSelectModels(scenarioId: string, identifiers: string | string[], emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
          const selection = this.selections[scenarioId];

          // Convert identifiers to modelIds for comparison (handles both URNs and modelIds)
          const identifierArray = !Array.isArray(identifiers) ? (identifiers ? [identifiers] : []) : identifiers;
          const modelIdsToRemove = this.toModelIds(identifierArray);

          // Remove modelIds from selection
          selection.modelIds = selection.modelIds.filter((id: string) => !modelIdsToRemove.includes(id));

          // Convert to URNs for viewer unload operation
          const urnsForViewer = this.modelIdsToUrns(modelIdsToRemove);
          if (urnsForViewer.length > 0) {
            await this.onUnload(scenarioId, selection.viewerId, urnsForViewer);
          }
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
        }

        resolve();
      })();
    });
  }

  unSelectAllModels(scenarioId: string, emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
          const selection = this.selections[scenarioId];

          const modelIds = selection.modelIds;

          if (modelIds.length > 0) {
            // Convert modelIds to URNs for viewer operations
            const urnsForViewer = this.modelIdsToUrns(modelIds);
            await this.onUnload(scenarioId, selection.viewerId, urnsForViewer);
            selection.modelIds = [];
          }
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT);
        }

        resolve();
      })();
    });
  }

  refreshViews(): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        await Promise.all(Object.keys(this.selections).map(async (key) => {
          const selection = this.selections[key];
          // Convert modelIds to URNs for viewer operations
          const urnsForViewer = this.modelIdsToUrns(selection.modelIds);
          await this.onLoad(key, selection.viewerId, urnsForViewer);
        }));

        resolve();
      })();
    });
  }
}
